import React from 'react';
import { mathFormat } from '@sg/utils/common';
import DbsTag from '@sg/components/Tag';
import EllipsisText from '@sg/components/EllipsisText';
import PassIcon from '@/pages/strategic/assests/svg/pass.svg';
import FailIcon from '@gp/assets/svg/goG/noGo.svg';
import RedoIcon from '@gp/assets/svg/goG/icon-revert.svg';
import PendingApprovalIcon from '@gp/assets/svg/goG/icon-shalou.svg';

export const DATA_ANALYTICS_METRICS_LIST = [
  'launch_time',
  'current_milestone',
  'spend_to_date',
  'total_dev_cost_estimated',
  'total_dev_cost_approved',
  'headcount_actual',
  'hc_planned_current',
  'hc_planned_next',
  'y1_copy_sales',
  'y2_copy_sales',
  'y3_copy_sales',
  'y1_gross_revenue',
  'y2_gross_revenue',
  'y3_gross_revenue',
  'ytm_total_project_cost',

  'ytm_dev_cost',
  'ytm_mkt_cost',
  'ytm_other_cost',

  'ytm_rd_staff_cost',

  'ytm_outsourcing_cost',
  'ytm_other_rd_cost',
  'ytm_ga_overheads',
  'ytm_vgtr',
];

// Default Head Rate Table Configuration
export const getDefaultHeadRateColumns = (activeDateTypeSegment: string, formatMessage: any) => [
  {
    header: activeDateTypeSegment === 'phase' ? formatMessage({ id: 'dbs_phase' }) : formatMessage({ id: 'dbs_month' }),
    dataIndex: 'date',
    key: 'date',
    width: 100,
  },
  {
    header: <div style={{ textAlign: 'right', width: '300px', textWrap: 'nowrap' }}>Total</div>,
    title: 'Total', // Plain text for download
    dataIndex: 'total',
    key: 'total',
    width: 120,
    render: (item: any) => (
      <div style={{ textAlign: 'right', width: '300px', textWrap: 'nowrap' }}>
        {item?.total === '' ? '-' : item?.total}
      </div>
    ),
  },
];

// Financials Table Configuration
export const getFinancialsColumns = (activeDateTypeSegment: string, formatMessage: any) => [
  {
    header: activeDateTypeSegment === 'phase' ? formatMessage({ id: 'dbs_phase' }) : formatMessage({ id: 'dbs_month' }),
    dataIndex: 'date',
    key: 'date',
    width: 150,
    render: (item: any) => (
      // Keep fixed column width while still exposing full text via tooltip on overflow.
      <EllipsisText text={item?.date || '-'} />
    ),
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>R&D Staff Cost</div>,
    title: 'R&D Staff Cost',
    dataIndex: 'rd_staff_cost',
    key: 'rd_staff_cost',
    format: mathFormat,
    align: 'right',
    width: 180,
    render: (item: any) => (
      <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>{mathFormat(item?.rd_staff_cost)}</div>
    ),
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>Outsourcing</div>,
    title: 'Outsourcing',
    dataIndex: 'outsourcing_cost',
    key: 'outsourcing_cost',
    format: mathFormat,
    align: 'right',
    width: 180,
    render: (item: any) => (
      <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>{mathFormat(item?.outsourcing_cost)}</div>
    ),
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>Other R&D</div>,
    title: 'Other R&D',
    dataIndex: 'other_rd_cost',
    key: 'other_rd_cost',
    format: mathFormat,
    align: 'right',
    width: 180,
    render: (item: any) => (
      <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>{mathFormat(item?.other_rd_cost)}</div>
    ),
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>G&A Overheads</div>,
    title: 'G&A Overheads',
    dataIndex: 'ga_overheads',
    key: 'ga_overheads',
    format: mathFormat,
    align: 'right',
    width: 180,
    render: (item: any) => (
      <div style={{ textAlign: 'right', textWrap: 'nowrap' }}>{mathFormat(item?.ga_overheads)}</div>
    ),
  },
  {
    header: <div style={{ textAlign: 'right', textWrap: 'nowrap', paddingRight: '20px' }}>VGTR</div>,
    title: 'VGTR',
    dataIndex: 'vgtr',
    key: 'vgtr',
    format: mathFormat,
    align: 'right',
    width: 200,
    render: (item: any) => (
      <div style={{ textAlign: 'right', textWrap: 'nowrap', paddingRight: '20px' }}>{mathFormat(item?.vgtr)}</div>
    ),
  },
];

// Chart Configuration Constants
export const DefaultChat = {
  icon: 'column-chart',
  value: 'bar',
  tip: 'Bar chart',
  format: true,
};

export const DefaultFinancialsChat = {
  icon: 'discount-chart',
  value: 'bar',
  tip: 'bar',
  format: false,
};

// Chart dimensions — table body maxHeight pairs with chart height (same pattern as Studios/config).
export const IN_DEV_CARD_CHART_HEIGHT = 399;
export const IN_DEV_CARD_TABLE_HEADER_ROW_HEIGHT = 50;
/** Manual fudge so tea-table--allbordered does not show a false scrollbar. */
export const IN_DEV_CARD_TABLE_BODY_MAX_HEIGHT_OFFSET = -8;

export const height = `${IN_DEV_CARD_CHART_HEIGHT}px`;

export interface IInDevCardTableMaxHeightOptions {
  tableHeaderRows?: 1 | 2;
}

export const getInDevCardChartHeight = (): string => `${IN_DEV_CARD_CHART_HEIGHT}px`;

/** scrollable maxHeight — tea-table (header + body) should match chart height */
export const getInDevCardTableBodyMaxHeight = (
  options: IInDevCardTableMaxHeightOptions = {},
): string => {
  const headerRows = options.tableHeaderRows ?? 1;
  // scrollable maxHeight applies to tbody only; subtract rendered <th> rows so
  // header + body ≈ chartHeight (th ≈ 50.67px with padding/borders, not 50).
  const bodyHeight =
    IN_DEV_CARD_CHART_HEIGHT
    - headerRows * IN_DEV_CARD_TABLE_HEADER_ROW_HEIGHT
    - IN_DEV_CARD_TABLE_BODY_MAX_HEIGHT_OFFSET;

  return `${Math.max(Math.round(bodyHeight * 100) / 100, 120)}px`;
};

export const showFinancialsList = ['bar', 'table'];
export const showRevenueTrendList = ['bar', 'table'];

// Chart Colors Configuration
// export const chartAllColorsObjs = {
//   Actual: '#5EC8F1',
//   Forecast: '#7F6FF1',
//   Budget: '#FC8B00',
//   kpi: '#FC8B00',
// };

// Segment Options
export const getOptionsSegment = (formatMessage: any) => [
  { text: formatMessage({ id: 'dbs_studios_by_phase' }), value: 'phase' },
  { text: formatMessage({ id: 'dbs_studios_by_month' }), value: 'month' },
];

// 反转getOptionsSegment
export const getReverseOptionsSegment = (formatMessage: any) => [
  { text: formatMessage({ id: 'dbs_studios_by_month' }), value: 'month' },
  { text: formatMessage({ id: 'dbs_studios_by_phase' }), value: 'phase' },
];
/**
 * Reusable function to render Gate Review Decision Tag
 * 渲染 Gate Review Decision 标签
 * @param gateReview - Gate review status (1: Pass, 2: Redo, 3: Fail, 4: Pending Approval)
 * @returns DbsTag component or null
 */
export const renderGateReviewTag = (gateReview: number | undefined) => {
  if (!gateReview) return null;

  switch (gateReview) {
    case 1: // Pass
      return (
        <DbsTag
          text="Pass"
          type="default"
          icon={<img src={PassIcon} alt="Pass" />}
          backgroundColor="#69D19C4D"
          className="dbs-gate-review-tag"
        />
      );
    case 2: // Redo
      return (
        <DbsTag
          text="Redo"
          // type="warn"
          icon={<img src={RedoIcon} alt="Redo" />}
          textColor="#F19E55"
          backgroundColor="#F19E551F"
          className="dbs-gate-review-tag"
        />
      );
    case 3: // Fail
      return (
        <DbsTag
          text="Fail"
          type="warn"
          icon={<img src={FailIcon} alt="Fail" />}
          backgroundColor="#EF58581F"
          className="dbs-gate-review-tag"
        />
      );
    case 4: // Pending Approval
      return (
        <DbsTag
          text="Pending Approval"
          // type="warn"
          icon={<img src={PendingApprovalIcon} alt="Pending Approval" />}
          textColor="#cfa92d"
          backgroundColor="#EDB6001F"
          className="dbs-gate-review-tag"
        />
      );
    default:
      return null;
  }
};

// Financials Table Columns Configuration
export const getFinancialsTableColumns = (formatMessage: any) => [
  {
    header: formatMessage({ id: 'dbs_date' }),
    dataIndex: 'date',
    key: 'date',
    width: 100,
  },
  {
    header: formatMessage({ id: 'dbs_dev_cost' }),
    dataIndex: 'Dev Cost',
    key: 'Dev Cost',
    width: 100,
    format: mathFormat,
    render: (item: any) => (
      <div style={{ textAlign: 'right', width: '55px', textWrap: 'nowrap' }}>
        {mathFormat(item?.['Dev Cost']) || '-'}
      </div>
    ),
  },
  {
    header: formatMessage({ id: 'dbs_mkt_cost' }),
    dataIndex: 'Mkt Cost',
    key: 'Mkt Cost',
    width: 100,
    format: mathFormat,
    render: (item: any) => (
      <div style={{ textAlign: 'right', width: '55px', textWrap: 'nowrap' }}>
        {mathFormat(item?.['Mkt Cost']) || '-'}
      </div>
    ),
  },
  {
    header: formatMessage({ id: 'dbs_other_cost' }),
    dataIndex: 'Other Cost',
    key: 'Other Cost',
    width: 100,
    format: mathFormat,
    render: (item: any) => (
      <div style={{ textAlign: 'right', width: '55px', textWrap: 'nowrap' }}>
        {mathFormat(item?.['Other Cost']) || '-'}
      </div>
    ),
  },
  {
    header: formatMessage({ id: 'dbs_revenue' }),
    dataIndex: 'Revenue',
    key: 'Revenue',
    width: 100,
    format: mathFormat,
    render: (item: any) => (
      <div style={{ textAlign: 'right', width: '55px', textWrap: 'nowrap' }}>{mathFormat(item?.Revenue) || '-'}</div>
    ),
  },
  {
    header: formatMessage({ id: 'dbs_cum_profit_break_even' }),
    dataIndex: 'Cum. Profit / Break even',
    key: 'Cum. Profit / Break even',
    width: 100,
    format: mathFormat,
    render: (item: any) => (
      <div style={{ textAlign: 'right', width: '170px', textWrap: 'nowrap' }}>
        {mathFormat(item?.['Cum. Profit / Break even']) || '-'}
      </div>
    ),
  },
];

// Financials Table Edit Columns Configuration
export const getFinancialsTableEditColumns = (
  formatMessage: any,
  InputNumberComponent: React.ComponentType<any>,
  cutoffYear?: number,
) => [
  {
    header: formatMessage({ id: 'dbs_date' }),
    dataIndex: 'date',
    key: 'date',
    width: 100,
  },
  {
    header: formatMessage({ id: 'dbs_dev_cost' }),
    dataIndex: 'Dev Cost',
    key: 'Dev Cost',
    width: 100,
    format: mathFormat,
    align: 'right',
    render: (
      record: any,
      rowKey: any,
      recordIndex: any,
      column: any,
      columnIndex: any,
      editData: any,
      stableUpdater: any,
      setEditData: any,
    ) => {
      // 优先从editData中获取已编辑的值
      const editedRecord = editData?.find((item: any) => item.id === record.id);
      const value = editedRecord?.['Dev Cost'] ?? record?.['Dev Cost'];

      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <InputNumberComponent
            value={value}
            onChange={(val: any) => {
              stableUpdater(val);
            }}
            record={record}
            width="55px"
            cutoffYear={cutoffYear}
          />
        </div>
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_mkt_cost' }),
    dataIndex: 'Mkt Cost',
    key: 'Mkt Cost',
    width: 100,
    format: mathFormat,
    align: 'right',
    render: (
      record: any,
      rowKey: any,
      recordIndex: any,
      column: any,
      columnIndex: any,
      editData: any,
      stableUpdater: any,
      setEditData: any,
    ) => {
      // 优先从editData中获取已编辑的值
      const editedRecord = editData?.find((item: any) => item.id === record.id);
      const value = editedRecord?.['Mkt Cost'] ?? record?.['Mkt Cost'];

      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <InputNumberComponent
            value={value}
            onChange={(val: any) => {
              stableUpdater(val);
            }}
            record={record}
            width="55px"
            cutoffYear={cutoffYear}
          />
        </div>
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_other_cost' }),
    dataIndex: 'Other Cost',
    key: 'Other Cost',
    width: 100,
    format: mathFormat,
    align: 'right',
    render: (
      record: any,
      rowKey: any,
      recordIndex: any,
      column: any,
      columnIndex: any,
      editData: any,
      stableUpdater: any,
      setEditData: any,
    ) => {
      // 优先从editData中获取已编辑的值
      const editedRecord = editData?.find((item: any) => item.id === record.id);
      const value = editedRecord?.['Other Cost'] ?? record?.['Other Cost'];

      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <InputNumberComponent
            value={value}
            onChange={(val: any) => {
              stableUpdater(val);
            }}
            record={record}
            width="55px"
            cutoffYear={cutoffYear}
          />
        </div>
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_revenue' }),
    dataIndex: 'Revenue',
    key: 'Revenue',
    width: 100,
    format: mathFormat,
    align: 'right',
    render: (
      record: any,
      rowKey: any,
      recordIndex: any,
      column: any,
      columnIndex: any,
      editData: any,
      stableUpdater: any,
      setEditData: any,
    ) => {
      // 优先从editData中获取已编辑的值
      const editedRecord = editData?.find((item: any) => item.id === record.id);
      const value = editedRecord?.Revenue ?? record?.Revenue;

      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <InputNumberComponent
            value={value}
            onChange={(val: any) => {
              stableUpdater(val);
            }}
            record={record}
            width="55px"
            cutoffYear={cutoffYear}
          />
        </div>
      );
    },
  },
  {
    header: formatMessage({ id: 'dbs_cum_profit_break_even' }),
    dataIndex: 'Cum. Profit / Break even',
    key: 'Cum. Profit / Break even',
    width: 100,
    format: mathFormat,
    align: 'right',
    render: (
      record: any,
      rowKey: any,
      recordIndex: any,
      column: any,
      columnIndex: any,
      editData: any,
      stableUpdater: any,
    ) => {
      // 优先从editData中获取已编辑的值
      const editedRecord = editData?.find((item: any) => item.id === record.id);
      const value = editedRecord?.['Cum. Profit / Break even'] ?? record?.['Cum. Profit / Break even'];

      return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <InputNumberComponent
            value={value}
            onChange={(val: any) => {
              stableUpdater(val);
            }}
            record={record}
            width="170px"
            cutoffYear={cutoffYear}
          />
        </div>
      );
    },
  },
];

